import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import invariant from "tiny-invariant";

import { callLcosService } from "../../data/lcosServices";
import { useAccessToken } from "../use-access-token";
import { useExternalPermissionFlow } from "../use-web-socket/use-external-permission-flow";
import { useCanFetchStaffMemberAccess } from "./use-can-fetch-staff-member-access";
import type { ExclusivePermissionCheck, PermissionPayload, StaffMemberPermissions } from "./types";

export type UseStaffMemberPermissionsOptions = {
  enabled?: boolean;
};

const queryKeys = {
  base: ["permissions"],
  detail: (options: { centerId: string; staffMemberId?: string }) => [...queryKeys.base, options] as const
} as const;

export const usePermissions = (options: {
  centerId: string;
  staffMemberId?: string;
  enabled?: boolean;
}) => {
  const { getToken } = useAccessToken();
  const hasRequiredIds = Boolean(options.centerId && options.staffMemberId);
  const canFetchByDefault = useCanFetchStaffMemberAccess(options.staffMemberId);
  const fetchAllowed =
    hasRequiredIds && (options.enabled !== undefined ? options.enabled : canFetchByDefault);

  return useQuery({
    queryKey: queryKeys.detail(options),
    queryFn: async () => {
      invariant(options.staffMemberId, "Staff member ID is required");

      return getStaffMemberPermissions(await getToken(), options.centerId, options.staffMemberId);
    },
    enabled: fetchAllowed,
    retry: false
  });
};


export const useStaffMemberPermissions = (
  centerId: string,
  staffMemberId: string,
  options?: UseStaffMemberPermissionsOptions
) => {
  const queryClient = useQueryClient();
  const { getToken } = useAccessToken();
  const { handleExternalPermissionFlow } = useExternalPermissionFlow();
  const hasRequiredIds = Boolean(centerId && staffMemberId);
  const canFetchByDefault = useCanFetchStaffMemberAccess(staffMemberId);
  const fetchAllowed =
    hasRequiredIds && (options?.enabled !== undefined ? options.enabled : canFetchByDefault);

  const queryKey = ["staffMemberPermissionsData", centerId, staffMemberId];

  const getStaffMemberPermissionData = async () => {
    if (!centerId || !staffMemberId) {
      return {} as unknown as StaffMemberPermissions;
    }

    return getStaffMemberPermissions(await getToken(), centerId, staffMemberId);
  };

  const staffMemberPositionsQuery = useQuery({
    queryKey,
    queryFn: getStaffMemberPermissionData,
    enabled: fetchAllowed,
    retry: false
  });

  const roleMutator = useMutation({
    mutationFn: async (newRole: { role: string }) =>
      updateStaffMemberRole(await getToken(), centerId, staffMemberId, newRole),

    onSuccess: newPermissions => {
      queryClient.invalidateQueries({ queryKey: ["centerStaffData", centerId] });
      return queryClient.setQueryData(queryKey, () => ({ ...newPermissions }));
    }
  });

  const permissionMutator = useMutation({
    mutationFn: async (newPermission: PermissionPayload) => {
      const accessToken = await getToken();
      // external flow runs async: we do NOT await it
      if (newPermission.isExternalPermission) {
        await queryClient.cancelQueries({ queryKey });
        void handleExternalPermissionFlow(accessToken, newPermission, centerId, staffMemberId, queryClient, queryKey);
        return;
      }
      // normal (non-external) API update
      return updateStaffMemberPermission(accessToken, centerId, staffMemberId, newPermission);
    },

    onMutate: async newPermission => {
      const previousData = structuredClone(queryClient.getQueryData(queryKey));

      if (newPermission.isExternalPermission) {
        queryClient.setQueryData(queryKey, (old: StaffMemberPermissions | undefined) => {
          if (!old) {
            return old;
          }

          const exists = old.permissions.some(p => p.action === newPermission.action);

          const updatedPermissions = exists
            ? old.permissions.map(p =>
                p.action === newPermission.action
                  ? {
                      ...p,
                      isAllowed: newPermission.isAllowed,
                      externalInProgress: true,
                      isExternalPermission: true
                    }
                  : p
              )
            : [
                ...old.permissions,
                {
                  action: newPermission.action,
                  isAllowed: newPermission.isAllowed,
                  isExternal: true,
                  fromRole: false,
                  externalInProgress: true,
                  isExternalPermission: true
                }
              ];

          return {
            ...old,
            permissions: updatedPermissions
          };
        });
      }

      return { previousData };
    },
    onSuccess: (data, variables) => {
      // only update the cache for non-external permissions
      if (!variables.isExternalPermission && data) {
        queryClient.setQueryData(queryKey, data);
      }
    },
    onError: () => {
      void queryClient.invalidateQueries(queryKey);
    }
  });

  const deletePermissionsMutator = useMutation({
    mutationFn: async () => deleteStaffMemberPermissions(await getToken(), centerId, staffMemberId),

    onSuccess: newPermissions => {
      return queryClient.setQueryData(queryKey, () => ({ ...newPermissions }));
    }
  });

  const staffMailboxMutator = useMutation({
    mutationFn: async (mailbox: { hasMailbox: boolean }) =>
      mailbox.hasMailbox
        ? createStaffMailbox(await getToken(), centerId, staffMemberId)
        : deleteStaffMailbox(await getToken(), centerId, staffMemberId),

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
    }
  });

  const changeRoleAndResetPermissionsMutator = useMutation({
    mutationFn: async (newRole: { role?: string }) => {
      await updateStaffMemberRole(await getToken(), centerId, staffMemberId, newRole);
      return deleteStaffMemberPermissions(await getToken(), centerId, staffMemberId);
    },
    onSuccess: newPermissions => {
      return queryClient.setQueryData(queryKey, () => ({ ...newPermissions }));
    }
  });

  const checkExclusivePermissionMutation = useMutation({
    mutationFn: async (permission: string) => {
      return checkExclusivePermission(await getToken(), centerId, staffMemberId, permission);
    }
  });

  return {
    staffMemberPermissions: staffMemberPositionsQuery.data,
    isLoading: fetchAllowed && staffMemberPositionsQuery.isLoading,
    roleMutator,
    permissionMutator,
    deletePermissionsMutator,
    staffMailboxMutator,
    changeRoleAndResetPermissionsMutator,
    checkExclusivePermission: checkExclusivePermissionMutation
  };
};

export const getStaffMemberPermissions = (accessToken: string, centerId: string, staffMemberId: string) => {
  return callLcosService<StaffMemberPermissions>(accessToken, `/api/staff/${centerId}/${staffMemberId}/access`);
};

export const checkExclusivePermission = (
  accessToken: string,
  centerId: string,
  staffMemberId: string,
  permission: string
) => {
  return callLcosService<ExclusivePermissionCheck>(
    accessToken,
    `/api/staff/${centerId}/${staffMemberId}/check-exclusive-center-permission?permission=${permission}`
  );
};

const updateStaffMemberRole = (
  accessToken: string,
  centerId: string | undefined,
  staffMemberId: string | undefined,
  payload: unknown
) => {
  return callLcosService<StaffMemberPermissions>(
    accessToken,
    `/api/staff/${centerId}/${staffMemberId}/role`,
    "PUT",
    payload
  );
};

const updateStaffMemberPermission = (
  accessToken: string,
  centerId: string,
  staffMemberId: string,
  payload: PermissionPayload
) => {
  return callLcosService<StaffMemberPermissions>(
    accessToken,
    `/api/staff/${centerId}/${staffMemberId}/permission`,
    "PUT",
    payload
  );
};

// TBC it deletes all a la carte granted permissions, permissions that are granted
// by the user's role remain in force
const deleteStaffMemberPermissions = (
  accessToken: string,
  centerId: string | undefined,
  staffMemberId: string | undefined
) => {
  return callLcosService<StaffMemberPermissions>(
    accessToken,
    `/api/staff/${centerId}/${staffMemberId}/permissions`,
    "DELETE"
  );
};

const createStaffMailbox = (accessToken: string, centerId: string | undefined, staffMemberId: string | undefined) => {
  return callLcosService(accessToken, `/api/staff/${centerId}/${staffMemberId}/mailbox`, "PUT");
};

const deleteStaffMailbox = (accessToken: string, centerId: string | undefined, staffMemberId: string | undefined) => {
  return callLcosService(accessToken, `/api/staff/${centerId}/${staffMemberId}/mailbox`, "DELETE");
};
